<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useAdminStore } from "@/store/admin.ts";
import { useLoaderStore } from "@/store/loader.ts";
import { getS3URL, generateInitialsAvatar } from "@/utils/helpers.ts";
import type { IListing } from "@/types.ts";
import NewListingCard from "../../components/admin/cards/NewListingCard.vue";
import EmptyDataPlaceholder from "../../components/common/EmptyDataPlaceholder.vue";
import Search from "../../assets/icons/search.svg";
import LoginAsModal from "../../components/admin/modals/LoginAsModal.vue";
import LoadingCard from "@/components/common/LoadingCard.vue";
import ExportExpertsAside from "../../components/admin/ExportExpertsAside.vue";
import { UiButton, UiIcon } from "@shopexperts/shared";

const adminStore = useAdminStore();
const loader = useLoaderStore();

const isLoading = computed(() => loader.isLoadingState);
const experts = computed(() => adminStore.experts);
const totalExperts = computed(() => adminStore.totalExperts);
const currentPage = computed(() => adminStore.currentPage);
const lastPage = computed(() => adminStore.lastPage);

const hasFilters = computed(() => {
  return status.value || typeOfAccount.value || role.value || 
         country.value || servicesOffered.value;
});

const [isLoadingMore, status, typeOfAccount, role, country, servicesOffered, searchQuery] = 
  [ref(false), ref(''), ref(''), ref(''), ref(''), ref(''), ref('')];

const isResetting = ref(false);

// Login As Modal
const showLoginAsModal = ref(false);
const selectedExpert = ref<IListing | null>(null);

// Export Experts Aside
const showExportAside = ref(false);

const openExportAside = () => {
  showExportAside.value = true;
};

const filterOptions = ref<{
  statuses: string[];
  roles: string[];
  countries: string[];
  expertTypes: string[];
  serviceCategories: string[];
  languages: string[];
  shopifyPlans: string[];
}>({
  statuses: [],
  roles: [],
  countries: [],
  expertTypes: [],
  serviceCategories: [],
  languages: [],
  shopifyPlans: []
});

// Login As functionality
const openLoginAsModal = (expert: any) => {
  selectedExpert.value = expert;
  showLoginAsModal.value = true;
}; 

const closeLoginAsModal = () => {
  showLoginAsModal.value = false;
  selectedExpert.value = null;
};

// Fetch filter options using admin store (NEW endpoint)
const fetchFilterOptions = async () => {
  try {
    const response = await adminStore.fetchNewExpertFilterOptions();
    filterOptions.value = response;
  } catch (error) {
    console.error('Error fetching filter options:', error);
  }
};

// Get current filters
const getCurrentFilters = () => ({
    search: searchQuery.value,
    status: status.value,
    expertType: typeOfAccount.value,
    serviceCategory: servicesOffered.value,
    role: role.value,
    country: country.value,
    page: currentPage.value
});

const fetchExperts = async (page = 1, resetData = false) => {
  if (isLoading.value && !isLoadingMore.value) return;
  if (!resetData) {
    isLoadingMore.value = true;
  }

  try {
    const params: any = {
      page,
    };

    if (searchQuery.value) params.search = searchQuery.value;
    if (status.value) params.status = status.value;
    if (country.value) params.country = country.value;
    if (typeOfAccount.value) params.expertType = typeOfAccount.value;
    if (servicesOffered.value) params.serviceCategory = servicesOffered.value;
    if (role.value) params.role = role.value;
    
    await adminStore.fetchNewExperts(params, !resetData && page > 1);
    
  } catch (error: any) {
    console.error('Error fetching experts:', error);
  } finally {
    isLoadingMore.value = false;
  }
};

const loadMore = () => {
  if (currentPage.value < lastPage.value && !isLoadingMore.value) {
    fetchExperts(currentPage.value + 1, false);
  }
};

const resetFilters = () => {
 isResetting.value = true;
 
  status.value = '';
  typeOfAccount.value = '';
  role.value = '';
  country.value = '';
  servicesOffered.value = '';
  searchQuery.value = '';
 
  fetchExperts(1, true);
 
  // Reset flag after a short delay
  setTimeout(() => {
    isResetting.value = false;
  }, 100);
};

const applyFilters = () => {
  fetchExperts(1, true);
};

let searchTimeout: ReturnType<typeof setTimeout> | undefined;
watch([searchQuery, status, role, typeOfAccount, servicesOffered, country], () => {
  // Skip if we're resetting
  if (isResetting.value) return;
  
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    applyFilters();
  }, 500);
});

onMounted(async () => {
  await fetchFilterOptions();
  fetchExperts(1, true);
});
</script>

<template>
  <main class="flex-1 p-8 overflow-y-auto bg-secondary font-light space-y-6">
    <div class="flex flex-row justify-between">
      <div>
        <h1>
          Listings <span class="text-gray-500">({{ totalExperts }})</span>
        </h1>
      </div>
      <div class="flex items-center gap-3">
        <UiButton 
          variant="outline" 
          label="Export"
          @click="openExportAside"
        >
          <template #before_label>
            <UiIcon name="upload" class="w-4 h-4" />
          </template>
        </UiButton>
        <div class="flex items-center border border-grey rounded-sm bg-white py-1 px-3 w-80 max-w-md shadow-xs">
          <Search />
          <input
              type="text"
              placeholder="Search by name"
              class="w-full ml-3 text-h4 outline-hidden placeholder-tertiary"
              v-model="searchQuery"
          />
        </div>
      </div>
    </div>

    <div class="mt-1 text-paragraph flex flex-wrap items-center gap-3">
      <!-- Status Filter (NEW VALUES) -->
      <select v-model="status" class="border rounded-sm px-1 w-auto py-2 text-h4 hover:bg-gray-100">
        <option value="">Status: All</option>
        <option 
          v-for="statusOption in filterOptions.statuses" 
          :key="statusOption" 
          :value="statusOption.toLowerCase()"
        >
          {{ statusOption }}
        </option>
      </select>

      <!-- Type of Account Filter -->
      <select v-model="typeOfAccount" class="border rounded-sm px-1 w-auto py-2 text-h4 hover:bg-gray-100">
        <option value="">Type of Account: All</option>
        <option 
          v-for="expertType in filterOptions.expertTypes" 
          :key="expertType" 
          :value="expertType.toLowerCase()"
        >
          {{ expertType }}
        </option>
      </select>

      <!-- Role Filter -->
      <select v-model="role" class="border rounded-sm px-1 w-auto py-2 text-h4 hover:bg-gray-100">
        <option value="">Role: All</option>
        <option 
          v-for="roleOption in filterOptions.roles" 
          :key="roleOption" 
          :value="roleOption"
        >
          {{ roleOption.charAt(0).toUpperCase() + roleOption.slice(1).replace('_', ' ') }}
        </option>
      </select>

      <!-- Country Filter -->
      <select v-model="country" class="border rounded-sm px-1 w-auto py-2 text-h4 hover:bg-gray-100">
        <option value="">Country: All</option>
        <option 
          v-for="countryOption in filterOptions.countries" 
          :key="countryOption" 
          :value="countryOption"
        >
          {{ countryOption }}
        </option>
      </select>

      <!-- Services Offered Filter -->
      <select v-model="servicesOffered" class="border rounded-sm px-1 w-auto py-2 text-h4 hover:bg-gray-100">
        <option value="">Services Offered: All</option>
        <option 
          v-for="serviceOption in filterOptions.serviceCategories" 
          :key="serviceOption" 
          :value="serviceOption"
        >
          {{ serviceOption }}
        </option>
      </select>

      <!-- Clear filters button -->
      <button 
        v-if="hasFilters" 
        @click="resetFilters"
        class="border rounded-sm px-3 py-1 text-h4 bg-gray-100 hover:bg-gray-200"
      >
        Clear All
      </button>
    </div>

    <!-- Loading State -->
    <LoadingCard v-if="isLoading" />

    <!-- Data State -->
    <div v-else-if="experts.length > 0">
      <NewListingCard
          v-for="listingItem in experts"
          :key="listingItem.id"
          :listing="listingItem"
          :current-filters="getCurrentFilters()"
          @openLoginModal="openLoginAsModal"
      />
      
      <div v-if="currentPage < lastPage" class="flex justify-center py-4">
        <button 
          @click="loadMore"
          :disabled="isLoadingMore"
          class="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
        >
          <span v-if="isLoadingMore">Loading...</span>
          <span v-else>Load More</span>
        </button>
      </div>
    </div>

    <!-- Empty State using EmptyDataPlaceholder component -->
    <div v-else>
      <EmptyDataPlaceholder
        title="Looks like you don't have any active listings yet."
        description="Your listings show up here soon."
      />
    </div>

    <!-- Login As Modal -->
    <LoginAsModal 
      v-if="showLoginAsModal && selectedExpert"
      :user="selectedExpert"
      :userId="selectedExpert.user_id"
      :userName="selectedExpert.display_name"
      :userEmail="selectedExpert.email"
      :userPhoto="selectedExpert.photo ? getS3URL(selectedExpert.photo) : null"
      :userAvatarInfo="selectedExpert.photo ? undefined : generateInitialsAvatar(selectedExpert.display_name)"
      modalTitle="Login as Expert"
      modalDescription="You are about to login as this expert. You will be redirected to their dashboard where you can view and manage their account."
      buttonText="Login As Expert"
      redirectPath="/expert/dashboard"
      @close="closeLoginAsModal"
    />

    <!-- Export Experts Aside -->
    <ExportExpertsAside v-model:open="showExportAside" />
  </main>
</template>

